import PropTypes from 'prop-types';

/**
 * Stopover behaviour labels (PM-177).
 *
 * The set is ADDITIVE and UNORDERED: a stop may carry any subset, and all sixteen subsets are
 * meaningful ({OPERATIONAL, SERVICE} is a real place - a truck stop that also loads).
 *
 * Absence is not "no behaviour": an absent, empty or wholly unrecognised set means OPERATIONAL.
 * The read model serialises the field with NON_EMPTY, so an untagged order carries no
 * `behaviourTags` key at all and every helper below must reproduce today's behaviour for it.
 */
export const StopBehaviourTag = {
  OPERATIONAL: 'OPERATIONAL',
  HANDOVER: 'HANDOVER',
  WAYPOINT: 'WAYPOINT',
  SERVICE: 'SERVICE',
};

const KNOWN_TAGS = Object.values(StopBehaviourTag);

/**
 * DISPLAY precedence, used only to pick a single glyph or chip out of a composite set.
 * It is deliberately NOT a domain ordering: nothing outside rendering may consult it, and it does
 * not reintroduce a governing stop type.
 */
const DISPLAY_PRECEDENCE = [
  StopBehaviourTag.OPERATIONAL,
  StopBehaviourTag.HANDOVER,
  StopBehaviourTag.SERVICE,
  StopBehaviourTag.WAYPOINT,
];

/**
 * The recognised labels a stop carries, in display-precedence order.
 * Unrecognised values are dropped rather than rendered, so a label added server-side before this
 * UI knows about it degrades to today's output instead of printing a raw key.
 * Returns [] when the stop is untagged - callers must treat that as OPERATIONAL.
 */
export const recognisedBehaviourTags = (stop) => (
  Array.isArray(stop && stop.behaviourTags)
    ? DISPLAY_PRECEDENCE.filter((tag) => stop.behaviourTags.indexOf(tag) !== -1)
    : []
);

/** True only when the stop explicitly carries at least one recognised label. */
export const hasBehaviourTags = (stop) => recognisedBehaviourTags(stop).length > 0;

/** The effective label set: the default {OPERATIONAL} when the stop is untagged. */
export const behaviourTagsOf = (stop) => {
  const tags = recognisedBehaviourTags(stop);
  return tags.length > 0 ? tags : [StopBehaviourTag.OPERATIONAL];
};

/**
 * The single label that drives the glyph / chip, under DISPLAY_PRECEDENCE.
 * An untagged stop yields OPERATIONAL, which every caller renders exactly as today.
 */
export const dominantBehaviourTag = (stop) => behaviourTagsOf(stop)[0];

/** A stop is operational unless it explicitly says otherwise. */
export const isOperationalStop = (stop) => (
  behaviourTagsOf(stop).indexOf(StopBehaviourTag.OPERATIONAL) !== -1
);

/** Carries more than one recognised label - drawn as a thin ring, never a second badge. */
export const isCompositeBehaviour = (stop) => recognisedBehaviourTags(stop).length > 1;

/*
 * The labels worth showing the user as chips. OPERATIONAL is dropped because it is the default every
 * untagged stop already carries - printing it would label the overwhelming majority of stops with
 * something that says nothing.
 *
 * Crucially a set difference, not a membership test: {OPERATIONAL, SERVICE} is legal and meaningful
 * (a truck stop that also loads) and must still show its SERVICE chip. Empty for an untagged stop.
 */
export const displayBehaviourTags = (stop) => recognisedBehaviourTags(stop)
  .filter((tag) => tag !== StopBehaviourTag.OPERATIONAL);

/*
 * The single label that replaces the load/unload marker glyph, or undefined when the stop keeps it.
 * Under DISPLAY_PRECEDENCE Operational outranks everything, so an operational stop - including every
 * untagged one - keeps the glyph it has always had. The one place this policy is decided.
 */
export const behaviourGlyphTag = (stop) => (
  isOperationalStop(stop) ? undefined : displayBehaviourTags(stop)[0]
);

/** True when any stop in the order is labelled; gates order-wide rendering changes. */
export const anyStopHasBehaviourTags = (stops) => (
  Array.isArray(stops) && stops.some(hasBehaviourTags)
);

/** i18n key for a label. Keys live under ORDER.STOP_TYPES.* alongside the load/unload kinds. */
export const behaviourTagLabelKey = (tag) => `ORDER.STOP_TYPES.${tag}`;

export const stopBehaviourTagPropTypes = PropTypes.oneOf(KNOWN_TAGS);

/**
 * Deliberately NOT PropTypes.arrayOf(oneOf(...)): the server may legitimately add a label this
 * build has never heard of, and a console warning per render is not the right response to that.
 */
export const stopBehaviourTagsPropTypes = PropTypes.arrayOf(PropTypes.string);
